import { updateThresholdThunk } from './rules';
import { THRESHOLD_UPDATE_TYPE } from '../constants';
import type { AppDispatch, State } from '~/store';
import { selectReasonCodeGroupById } from '../selectors';

/** Factory API for threshold updates. */
export const updateThreshold = {
  /** Update the comparison operator (e.g., 'gt', 'lt', 'e') */
  operator: (thresholdId: string, operator: string) => updateThresholdThunk({
    thresholdId,
    update: {
      type: THRESHOLD_UPDATE_TYPE.OPERATOR,
      value: operator,
    },
  }),

  /** Update the left-side action */
  leftAction: (thresholdId: string, actionId: string) => updateThresholdThunk({
    thresholdId,
    update: { type: THRESHOLD_UPDATE_TYPE.LEFT_ACTION, actionId },
  }),

  /** Update the left-side attribute (for service actions) */
  leftAttribute: (thresholdId: string, attributeId: number) => updateThresholdThunk({
    thresholdId,
    update: { type: THRESHOLD_UPDATE_TYPE.LEFT_ATTRIBUTE, attributeId },
  }),

  /** Update the right-side action for dynamic comparison */
  rightAction: (thresholdId: string, actionId: string) => updateThresholdThunk({
    thresholdId,
    update: { type: THRESHOLD_UPDATE_TYPE.RIGHT_ACTION, actionId },
  }),

  /** Update the right-side attribute for dynamic service action comparison */
  rightDynamicAttribute: (thresholdId: string, attributeId: number) => updateThresholdThunk({
    thresholdId,
    update: { type: THRESHOLD_UPDATE_TYPE.RIGHT_ATTRIBUTE_ID, attributeId },
  }),

  /** Update the right-side static value */
  rightValue: (
    thresholdId: string,
    value: string | number | boolean | null,
  ) => updateThresholdThunk({
    thresholdId,
    update: {
      type: THRESHOLD_UPDATE_TYPE.RIGHT_ATTRIBUTE_VALUE,
      value,
    },
  }),

  /**
   * Update the reason code group for reason code comparisons. Looks the group up
   * from state by its id, then applies both the group id and the resolved group
   * to the threshold as a single change.
   */
  reasonCodeGroup: (thresholdId: string, reasonCodeGroupId: number) => (
    dispatch: AppDispatch,
    getState: () => State,
  ) => {
    const reasonCodeGroup = selectReasonCodeGroupById(reasonCodeGroupId)(getState());
    if (!reasonCodeGroup) {
      return;
    }

    // todo: consolidate these into a single updateThresholdThunk call
    dispatch(updateThresholdThunk({
      thresholdId,
      update: {
        type: THRESHOLD_UPDATE_TYPE.REASON_CODE_GROUP_ID,
        value: reasonCodeGroupId,
      },
    }));

    dispatch(updateThresholdThunk({
      thresholdId,
      update: {
        type: THRESHOLD_UPDATE_TYPE.REASON_CODE_GROUP,
        value: reasonCodeGroup,
      },
    }));
  },

  /** Update the left tag action result for tag-based thresholds */
  leftTagResult: (thresholdId: string, result: boolean) => updateThresholdThunk({
    thresholdId,
    update: {
      type: THRESHOLD_UPDATE_TYPE.RIGHT_LEFT_TAG_RESULT,
      value: result,
    },
  }),
} as const;

export { updateThresholdThunk };
